import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Input, Button, Space, Alert, Typography, Divider, message } from "antd";
import {
    ScanOutlined, CameraOutlined, StopOutlined, EnterOutlined
} from "@ant-design/icons";

const { Text } = Typography;

// Common product barcode formats (1D + the usual 2D ones). Resolved against the
// library enum after it is loaded on demand.
const SUPPORTED_FORMAT_NAMES = [
    "EAN_13", "EAN_8", "UPC_A", "UPC_E", "CODE_128",
    "CODE_39", "CODE_93", "ITF", "CODABAR", "QR_CODE", "DATA_MATRIX",
];

const SCAN_CONFIG = {
    fps: 10,
    qrbox: { width: 280, height: 150 },
    aspectRatio: 1.333334,
};

/**
 * Barcode capture modal.
 *
 * Supports two capture paths:
 *   1. Hardware wedge scanners (USB / Bluetooth) - they behave like a keyboard,
 *      so the focused capture input receives the code and the trailing Enter
 *      key submits it.
 *   2. Device camera - decoded in-browser via html5-qrcode (zxing based), so it
 *      works on platforms where the native BarcodeDetector API is unavailable
 *      (e.g. desktop Chrome on Windows).
 *
 * @param {boolean}  open       - whether the modal is visible
 * @param {Function} onClose    - called when the modal should close
 * @param {Function} onDetected - called with the decoded barcode string
 */
const BarcodeScannerModal = ({ open, onClose, onDetected }) => {
    const [manualCode, setManualCode] = useState("");
    const [cameraScanning, setCameraScanning] = useState(false);
    const [cameraError, setCameraError] = useState("");

    const scannerRef = useRef(null);
    const handledRef = useRef(false);
    const inputRef = useRef(null);
    // Unique per mount so multiple modals never share a reader element id.
    const regionIdRef = useRef(`barcode-scan-region-${Math.random().toString(36).slice(2)}`);
    // html5-qrcode is ~110 kB gzipped, so it is loaded on demand (only when the
    // scanner is actually used) instead of shipping in the main bundle.
    const scannerLibRef = useRef(null);

    const loadScannerLib = useCallback(() => {
        if (!scannerLibRef.current) {
            scannerLibRef.current = import("html5-qrcode");
        }
        return scannerLibRef.current;
    }, []);

    /* ─── camera lifecycle ───────────────────────────────────── */
    const stopCamera = useCallback(async () => {
        const scanner = scannerRef.current;
        if (!scanner) {
            setCameraScanning(false);
            return;
        }

        try {
            if (scanner.isScanning) {
                await scanner.stop();
            }
        } catch {
            // Ignore: the element may already be torn down.
        }

        try {
            scanner.clear();
        } catch {
            // Ignore: clear() throws when nothing was rendered.
        }

        scannerRef.current = null;
        setCameraScanning(false);
    }, []);

    const startCamera = useCallback(async () => {
        setCameraError("");
        try {
            const { Html5Qrcode, Html5QrcodeSupportedFormats } = await loadScannerLib();

            if (!scannerRef.current) {
                const formatsToSupport = SUPPORTED_FORMAT_NAMES
                    .map((name) => Html5QrcodeSupportedFormats[name])
                    .filter((value) => value !== undefined);

                scannerRef.current = new Html5Qrcode(regionIdRef.current, {
                    formatsToSupport,
                    useBarCodeDetectorIfSupported: true,
                    verbose: false,
                });
            }

            const scanner = scannerRef.current;

            const onSuccess = (decodedText) => {
                if (handledRef.current) return;
                handledRef.current = true;
                const value = String(decodedText || "").trim();
                stopCamera().finally(() => onDetected?.(value));
            };
            const onFailure = () => { /* frame without a code - keep scanning */ };

            try {
                // Preferred: ask for the rear camera (mobile / tablets).
                await scanner.start({ facingMode: "environment" }, SCAN_CONFIG, onSuccess, onFailure);
            } catch {
                // Desktop webcams often reject facingMode - fall back to a device id.
                const cameras = await Html5Qrcode.getCameras();
                if (!cameras || cameras.length === 0) {
                    throw new Error("No camera was found on this device.");
                }
                const preferred =
                    cameras.find((cam) => /back|rear|environment/i.test(cam.label || "")) ||
                    cameras[cameras.length - 1];
                await scanner.start(preferred.id, SCAN_CONFIG, onSuccess, onFailure);
            }

            setCameraScanning(true);
        } catch (err) {
            scannerRef.current = null;
            setCameraScanning(false);
            const permissionDenied =
                err?.name === "NotAllowedError" || err?.name === "NotReadableError";
            setCameraError(
                permissionDenied
                    ? "Camera permission was denied. Allow camera access and try again."
                    : (err?.message || "Unable to start the camera on this device.")
            );
        }
    }, [loadScannerLib, onDetected, stopCamera]);

    /* ─── reset + cleanup on open/close ──────────────────────── */
    useEffect(() => {
        if (open) {
            handledRef.current = false;
            setManualCode("");
            setCameraError("");
            // Warm the scanner chunk so "Start Camera" feels instant.
            loadScannerLib().catch(() => { /* retried on demand */ });
            const timer = setTimeout(() => inputRef.current?.focus(), 150);
            return () => clearTimeout(timer);
        }

        stopCamera();
        return undefined;
    }, [open, stopCamera, loadScannerLib]);

    // Tear down the camera if the modal is unmounted while scanning.
    useEffect(() => () => { stopCamera(); }, [stopCamera]);

    const handleClose = async () => {
        await stopCamera();
        onClose?.();
    };

    const submitManual = () => {
        const value = manualCode.trim();
        if (!value) {
            message.warning("Scan or type a barcode first.");
            inputRef.current?.focus();
            return;
        }
        handledRef.current = true;
        onDetected?.(value);
    };

    return (
        <Modal
            title={
                <Space>
                    <ScanOutlined style={{ color: "var(--fluid-primary)" }} />
                    Scan Barcode
                </Space>
            }
            open={open}
            onCancel={handleClose}
            footer={
                <Space>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button type="primary" icon={<EnterOutlined />} onClick={submitManual}>
                        Use Code
                    </Button>
                </Space>
            }
            width={620}
            destroyOnClose
        >
            {/* Hardware wedge scanner */}
            <div style={{ marginBottom: 8 }}>
                <Text strong>Hardware scanner</Text>
                <Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 8 }}>
                    Point your USB or Bluetooth scanner at the barcode. It types the code into the
                    box below and the trailing Enter captures it.
                </Text>
                <Input
                    ref={inputRef}
                    size="large"
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    onPressEnter={submitManual}
                    placeholder="Waiting for scan… (you can also type the code)"
                    prefix={<ScanOutlined />}
                    allowClear
                />
            </div>

            <Divider plain style={{ margin: "16px 0 12px" }}>or scan with the camera</Divider>

            {/* Camera scanner */}
            {cameraError && (
                <Alert
                    type="warning"
                    showIcon
                    message={cameraError}
                    style={{ marginBottom: 12 }}
                />
            )}

            <div
                id={regionIdRef.current}
                style={{
                    width: "100%",
                    minHeight: cameraScanning ? 240 : 0,
                    borderRadius: 10,
                    overflow: "hidden",
                    background: cameraScanning ? "#000" : "transparent",
                }}
            />

            <Space style={{ marginTop: 12 }}>
                {!cameraScanning ? (
                    <Button type="primary" icon={<CameraOutlined />} onClick={startCamera}>
                        Start Camera
                    </Button>
                ) : (
                    <Button danger icon={<StopOutlined />} onClick={stopCamera}>
                        Stop Camera
                    </Button>
                )}

            </Space>
        </Modal>
    );
};

export default BarcodeScannerModal;
